import _ from 'lodash';
import { StateTree, defineStore } from 'pinia';

import { EformsConstants } from '@/main/constants/eforms-constants';
import { Komponent } from '@/main/enums/komponent.enum';
import { KonfigureretContent } from '@/main/models/eforms/KonfigureretContent';
import { KonfigureretNoticeType } from '@/main/models/eforms/KonfigureretNoticeType';
import { AendringsbekendtgoerelseSetting } from '@/main/models/eforms/aendringsbekendtgoerelseSetting';
import { ChoiceGroupLookup } from '@/main/models/eforms/choiceGroupLookup';
import { KonfigureretXmlStructure } from '@/main/models/eforms/konfigureretXmlStructure';
import {
  BekendtgoerelseNoegle,
  Content,
  EgenBekendtgoerelseDto,
  Field,
  FieldKonfiguration,
  FieldsModel,
  GroupKonfiguration,
  Metadata,
  NoticeType,
  NoticeTypeKonfiguration,
  NoticeTypeList,
  VisualModelNode
} from '@/main/models/generated';
import { bekendtgoerelseService } from '@/main/services/bekendtgoerelse.service';
import { bekendtgoerelseKladdeService } from '@/main/services/eforms/bekendtgoerelse-kladde.service';
import { eformsFormularKonfigurationService } from '@/main/services/eforms/eforms-formular-konfiguration.service';
import { getBekendtgoerelseController } from '@/main/services/generated/bekendtgoerelse-controller/bekendtgoerelse-controller';
import { getFormularKonfigurationController } from '@/main/services/generated/formular-konfiguration-controller/formular-konfiguration-controller';
import { getSdkController } from '@/main/services/generated/sdk-controller/sdk-controller';
import AuthService from '@/main/services/s9-oidc/auth.service';
import { DEFAULT_NOTICE_TYPE, DEFAULT_SDK_VERSION, SDK_VERSION_VALGMULIGHEDER } from '@/main/stores/sdkVersionValgmuligheder';
import { dateUtil } from '@/main/utils/date-util';
import { eformsDataUtil } from '@/main/utils/eforms-data-util';
import { skalFejlFiltreres } from '@/main/utils/fejl-filtrering-util';
import { eformsUtil } from '@/main/utils/eforms-util';
import { eformsValidationUtil } from '@/main/utils/eforms-validation-util';
import { log } from '@/main/utils/logger-util';
import { FieldTerms } from '@/main/enums/eforms/eformsFieldTerms.enum';

interface EFormsStoreModel {
  model: VisualModelNode;
  bekendtgoerelseNoegle?: BekendtgoerelseNoegle;
  currentLanguageId?: string;
  highestContentCountMap: Record<string, number>;
  sdkVersion: string;
  noticeSubTypeId: string;
  noticeSubType?: NoticeType;
  noticeSubTypeList?: NoticeTypeList;
  developerMode: boolean;
  visKunObligatoriskeFelter: boolean;
  visBusinessTermId: boolean;
  aendringsbekendtgoerelse: AendringsbekendtgoerelseSetting;
  f: {
    data?: FieldsModel;
    dataMap?: Map<string, Field>;
    xmlMap?: Map<string, KonfigureretXmlStructure>;
    promise?: Promise<void> | null;
    loading: boolean;
    error?: any;
  };
  noticeTypeKonfiguration?: NoticeTypeKonfiguration;
  noticeTypeKonfigurationGroups?: GroupKonfiguration[];
  noticeTypeKonfigurationFields?: FieldKonfiguration[];
  konfigureretNoticeType?: KonfigureretNoticeType;
  choiceGroupLookupHelper?: Record<string, ChoiceGroupLookup>;
  initPromise?: Promise<void> | null;
  formularValideringer: ValideringFejl;
  expandedAccordions: Map<string, boolean>;
  kladde: {
    lastSaveStartTime?: number;
    lastSavedAt?: number;
    lastSavedKladde?: string;
    continuousErrorCount: number;
    scheduled?: boolean;
  };
  genbrug?: {
    egneBekendtgoerelserMetadata?: EgenBekendtgoerelseDto[];
    valgtBekentgoerelseKey?: string;
    hasAutofilledCvr?: boolean;
  };
}

export interface ValideringFejl {
  feltFejl?: Map<string, ValideringFejlDetailer>;
  andreFejl?: ValideringFejlDetailer[];
  erValideringKoert?: boolean;
  erValideringUdenFejl?: boolean;
  erKompletFormularValideringTil: boolean;
}

export interface ValideringFejlDetailer {
  ruleId: string;
  details: string;
}

interface StoredEformsStoreModel {
  sdkVersion: EFormsStoreModel['sdkVersion'];
  noticeSubTypeId: EFormsStoreModel['noticeSubTypeId'];
  visBusinessTermId: EFormsStoreModel['visBusinessTermId'];
  aendringsbekendtgoerelse: EFormsStoreModel['aendringsbekendtgoerelse'];
  developerMode: EFormsStoreModel['developerMode'];
  visKunObligatoriskeFelter: EFormsStoreModel['visKunObligatoriskeFelter'];
  expandedAccordions: [string, boolean][];
}

const defaultValue: EFormsStoreModel = {
  model: {},
  highestContentCountMap: {},
  currentLanguageId: EformsConstants.Languages.DEFAULT, // Sprog default
  sdkVersion: DEFAULT_SDK_VERSION,
  noticeSubTypeId: DEFAULT_NOTICE_TYPE,
  visBusinessTermId: false,
  developerMode: false,
  visKunObligatoriskeFelter: true,
  aendringsbekendtgoerelse: AendringsbekendtgoerelseSetting.IKKE_AENDRINGSBEKENDTGOERELSE,
  f: {
    data: undefined,
    error: null,
    loading: false,
    promise: undefined
  },
  formularValideringer: {
    erValideringKoert: false,
    erKompletFormularValideringTil: false
  },
  expandedAccordions: new Map(),
  kladde: {
    continuousErrorCount: 0,
    scheduled: false
  },
  genbrug: {
    egneBekendtgoerelserMetadata: undefined,
    valgtBekentgoerelseKey: undefined,
    hasAutofilledCvr: false
  }
};

export const useEformsModelStore = defineStore('eformsModelStore', {
  state: (): EFormsStoreModel => defaultValue,
  persist: {
    storage: localStorage,
    serializer: {
      serialize: (value: StateTree) =>
        JSON.stringify({
          sdkVersion: value.sdkVersion,
          noticeSubTypeId: value.noticeSubTypeId,
          visBusinessTermId: value.visBusinessTermId,
          developerMode: value.developerMode,
          visKunObligatoriskeFelter: value.visKunObligatoriskeFelter,
          aendringsbekendtgoerelse: value.aendringsbekendtgoerelse,
          expandedAccordions: Array.from(value.expandedAccordions.entries())
        } as StoredEformsStoreModel),
      deserialize: (value: string) => {
        const restored: StoredEformsStoreModel = JSON.parse(value);
        if (SDK_VERSION_VALGMULIGHEDER.findIndex(x => x.value === restored.sdkVersion) === -1) {
          log(`Gemt SDK-version: ${restored.sdkVersion} er ikke længere supporteret, resetter ...`);
          return defaultValue;
        }
        return {
          sdkVersion: restored.sdkVersion,
          noticeSubTypeId: restored.noticeSubTypeId,
          visBusinessTermId: restored.visBusinessTermId ?? defaultValue.visBusinessTermId,
          developerMode: restored.developerMode ?? defaultValue.developerMode,
          visKunObligatoriskeFelter: restored.visKunObligatoriskeFelter ?? defaultValue.visKunObligatoriskeFelter,
          aendringsbekendtgoerelse: restored.aendringsbekendtgoerelse ?? defaultValue.aendringsbekendtgoerelse,
          f: {
            error: null,
            loading: false
          },
          formularValideringer: {
            erValideringKoert: false
          },
          expandedAccordions: restored.expandedAccordions !== undefined ? new Map(restored.expandedAccordions) : new Map()
        } as EFormsStoreModel;
      }
    },
    afterRestore: ctx => {
      console.debug(`afterRestore: ${ctx.store.$id}`);
      ctx.store.$state.f = {
        data: undefined,
        error: null,
        loading: false,
        promise: undefined
      };
    }
  },
  actions: {
    // Initialiserer notice type konfiguration fra backend hvis den ikke allerede er hentet
    async initNoticeTypeKonfiguration(): Promise<void> {
      if (this.noticeTypeKonfiguration != undefined) {
        return;
      }
      const config = AuthService.getConfig(Komponent.Udbud);
      const ntkData = (await getFormularKonfigurationController().hentNoticeTypeKonfiguration(config)).data;
      this.noticeTypeKonfiguration = ntkData;
      this.noticeTypeKonfigurationGroups = [...(ntkData.groups?.added || []), ...(ntkData.groups?.overwritten || [])];
      this.noticeTypeKonfigurationFields = [...(ntkData.fields?.added || []), ...(ntkData.fields?.overwritten || [])];
    },
    // Initialiserer felter fra SDK hvis de ikke allerede er hentet eller hvis SDK version er ændret
    async initFields(newSdkVersion?: string): Promise<void> {
      if ((newSdkVersion && newSdkVersion != this.sdkVersion) || this.f.data == undefined) {
        // Undgå at hente data mere end en gang...
        if (this.f.data != undefined && this.sdkVersion == newSdkVersion) {
          return;
        }
        const versionToFetch = newSdkVersion ?? this.sdkVersion;

        if (!this.f.promise) {
          this.f.loading = true;
          const config = AuthService.getConfig(Komponent.Udbud);
          this.f.promise = getSdkController()
            .hentFieldsModelJson(versionToFetch, config)
            .then(response => {
              this.f.data = response.data;
              this.f.dataMap = new Map(this.f.data.fields.map(field => [field.id, field]));
              // Undlad at assign xmlMap til pinia-store før vi har brug for det for at undgå change detection.
              const xmlMap = eformsDataUtil.beregnXmlMap(response.data.xmlStructure, response.data.fields);
              // Assign ind i pinia store.
              this.f.xmlMap = xmlMap;
              this.f.error = null;
              this.sdkVersion = versionToFetch;
            })
            .catch(error => {
              this.f.error = error;
            })
            .finally(() => {
              this.f.loading = false;
              this.f.promise = null;
            });
        }

        return this.f.promise;
      }
    },
    // Initialiserer listen over notice types fra SDK, baseret på newSdkVersion som angiver hvor der skal hentes data fra
    async initNoticeTypeList(newSdkVersion?: string): Promise<void> {
      if (this.noticeSubTypeList != undefined && this.sdkVersion == newSdkVersion) {
        // Intet er nyt, undlad at gøre noget.
        return;
      }
      const config = AuthService.getConfig(Komponent.Udbud);
      const response = await getSdkController().listNoticeTypes(newSdkVersion ?? this.sdkVersion, config);
      this.noticeSubTypeList = response.data;
      console.debug('Hentede: noticeSubTypeList');
    },
    // Initialiserer og sætter den aktuelle notice type
    // @param newSdkVersion - SDK version
    // @param noticeTypeId - ID på notice type der skal hentes
    async initAndSetNoticeType(newSdkVersion: string, noticeTypeId?: string): Promise<void> {
      if (this.noticeSubType != undefined && this.noticeSubTypeId == noticeTypeId && this.sdkVersion == newSdkVersion) {
        // Intet er nyt, undlad at gøre noget.
        return;
      }

      // Fetch notice type
      const config = AuthService.getConfig(Komponent.Udbud);
      const response = await getSdkController().hentNoticeType(newSdkVersion, noticeTypeId ?? this.noticeSubTypeId, config);

      this.noticeSubType = response.data;
      this.noticeSubTypeId = this.noticeSubType.noticeId;
    },
    // Initialiserer listen over brugerens egne bekendtgørelser til genbrug
    async initEgneBekendtgoerelser(): Promise<void> {
      if (this.genbrug?.egneBekendtgoerelserMetadata !== undefined) {
        return;
      }

      try {
        const response = await bekendtgoerelseService.hentEgneBekendtgoerelser();
        if (response !== undefined) {
          this.genbrug = {
            egneBekendtgoerelserMetadata: response,
            valgtBekentgoerelseKey: this.genbrug?.valgtBekentgoerelseKey
          };
        }
      } catch (err) {
        console.warn(`Kunne ikke hente egne bekendtgørelser: ${JSON.stringify(err)}`);
      }
    },
    // Håndterer intialisering af alle afhængigheder
    async init(
      noticeTypeId?: string,
      sdkVersion?: string,
      forceRebuildModel?: boolean,
      erAendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting,
      aendringsbekendtgoerelseAf?: BekendtgoerelseNoegle,
      kladdeBekendtgoerelseNoegle?: BekendtgoerelseNoegle,
      defaultValues?: Record<string, string> | undefined
    ): Promise<void> {
      // Undgå at init kan køre mere end en gang samtidig
      if (this.initPromise) {
        return this.initPromise;
      }
      let promiseResolve: (value: void | PromiseLike<void>) => void;
      this.initPromise = new Promise(resolve => {
        promiseResolve = resolve;
      });

      try {
        const skalBrugeKladde = this.skalBrugeKladde(kladdeBekendtgoerelseNoegle);

        // Bestem parametre for initialisering
        const initParams = this.bestemInitParametre(
          noticeTypeId,
          sdkVersion,
          forceRebuildModel,
          erAendringsbekendtgoerelse,
          aendringsbekendtgoerelseAf
        );

        // Håndter kladde hvis nødvendigt
        if (skalBrugeKladde) {
          await this.initialiserFraKladde(kladdeBekendtgoerelseNoegle!, noticeTypeId, erAendringsbekendtgoerelse);
        }

        // Initialiser alle afhængigheder
        await this.initialiserAlleAfhaengigheder(initParams.newSdkVersion, noticeTypeId);

        // Byg konfigureret notice type hvis nødvendigt
        if (initParams.skalRebuildModel || this.konfigureretNoticeType == undefined) {
          this.bygKonfigureretNoticeType();
        }

        // hvis der haves ntk og choiceGroupLookupHelper ikke er initialiseret endnu
        if (this.noticeTypeKonfiguration && (initParams.skalRebuildModel || !this.choiceGroupLookupHelper)) {
          this.bygChoiceGroupLookupHelper(this.noticeTypeKonfiguration);
        }

        // Byg og konfigurer model hvis nødvendigt
        if (initParams.skalRebuildModel && !skalBrugeKladde) {
          await this.bygOgKonfigurerModel(defaultValues, erAendringsbekendtgoerelse);
        }

        // Håndter ændringsbekendtgørelse logik
        await this.haandterAendringsbekendtgoerelsesLogik(aendringsbekendtgoerelseAf, skalBrugeKladde);

        // Afslut initialisering
        await this.afslutInitialisering(kladdeBekendtgoerelseNoegle);

        // Resolve promise, så alle der venter kan fortsætte
        promiseResolve!();
      } finally {
        this.initPromise = null;
      }
    },
    // Bestem hvilke parametre der skal bruges til initialisering
    bestemInitParametre(
      noticeTypeId?: string,
      sdkVersion?: string,
      forceRebuildModel?: boolean,
      erAendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting,
      aendringsbekendtgoerelseAf?: BekendtgoerelseNoegle
    ) {
      // Bestem om model skal genopbygges baseret på ændringer i parametre
      const skalRebuildModel =
        forceRebuildModel ||
        (sdkVersion != undefined && sdkVersion != this.sdkVersion) ||
        (noticeTypeId != undefined && noticeTypeId != this.noticeSubTypeId) ||
        this.model.contentId == undefined ||
        (erAendringsbekendtgoerelse != undefined && erAendringsbekendtgoerelse != this.aendringsbekendtgoerelse) ||
        aendringsbekendtgoerelseAf !== undefined;

      const newSdkVersion = sdkVersion ?? this.sdkVersion;
      this.aendringsbekendtgoerelse = erAendringsbekendtgoerelse ?? this.aendringsbekendtgoerelse;

      return { skalRebuildModel, newSdkVersion };
    },
    // Tjek om vi skal bruge en eksisterende kladde
    skalBrugeKladde(kladdeBekendtgoerelseNoegle?: BekendtgoerelseNoegle): boolean {
      return kladdeBekendtgoerelseNoegle?.noticeId?.value !== undefined &&
        kladdeBekendtgoerelseNoegle.noticeId.value !== '' &&
        kladdeBekendtgoerelseNoegle?.noticeVersion?.value !== undefined &&
        kladdeBekendtgoerelseNoegle?.noticeVersion.value !== '';
    },
    // Initialiser fra eksisterende kladde
    async initialiserFraKladde(
      kladdeBekendtgoerelseNoegle: BekendtgoerelseNoegle,
      noticeTypeId?: string,
      erAendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting
    ) {
      const kladdeVm = await bekendtgoerelseKladdeService.hentKladde(kladdeBekendtgoerelseNoegle);
      if (noticeTypeId !== kladdeVm.noticeSubType) {
        const fejlbesked = `NoticeSubType på kladde: ${this.noticeSubTypeId} er forskellig fra den vi er ved at loade: ${kladdeVm.noticeSubType}`;
        console.error(fejlbesked);
        throw new Error(fejlbesked);
      }

      // Gendan tilstand fra kladde
      this.model = kladdeVm;
      this.kladde.lastSavedAt = Date.now() - bekendtgoerelseKladdeService.SAVE_INTERVAL_IN_MS;
      this.highestContentCountMap = initHighestContentCountMap(this.model);
      console.debug(
        `Gendannede state fra kladde: '${kladdeBekendtgoerelseNoegle.noticeId?.value}' '${kladdeBekendtgoerelseNoegle.noticeVersion?.value}'`
      );
      this.saetNoticeId();

      // Håndter ændringsbekendtgørelse indstillinger fra kladde
      if (this.model.aendringsbekendtgoerelseSetting !== undefined) {
        console.debug(
          'Kladde har aendringsbekendtgoerelseSetting er sat til: ' +
            this.model.aendringsbekendtgoerelseSetting +
            ' init kald har: ' +
            erAendringsbekendtgoerelse
        );
        if (this.model.aendringsbekendtgoerelseSetting !== erAendringsbekendtgoerelse) {
          console.debug('Kladde og init-kald stemmer ikke overens. Anvender kladdeens indstilling.');
        }
      }
    },
    // Initialiser alle nødvendige afhængigheder parallelt
    async initialiserAlleAfhaengigheder(newSdkVersion: string, noticeTypeId?: string) {
      // Start alle asynkrone operationer parallelt
      const fieldsPromise = this.initFields(newSdkVersion);
      const noticeSubTypeListPromise = this.initNoticeTypeList(newSdkVersion);
      const ntkPromise = this.initNoticeTypeKonfiguration();
      const egneBekendtgoerelserPromise = this.initEgneBekendtgoerelser();

      // Hent notice type synkront (skal være færdig før vi fortsætter)
      await this.initAndSetNoticeType(newSdkVersion, noticeTypeId);
      if (this.noticeSubType == null) {
        throw new Error('Notice sub type skal være sat nu ellers gik noget grueligt galt!');
      }

      // Opdater SDK version
      this.sdkVersion = newSdkVersion;

      // Vent på at alle parallelle operationer er færdige
      await fieldsPromise;
      await noticeSubTypeListPromise;
      await ntkPromise;
      await egneBekendtgoerelserPromise;
    },

    // Byg den konfigurerede notice type
    bygKonfigureretNoticeType() {
      this.konfigureretNoticeType = eformsFormularKonfigurationService.konfigurerNoticeType(
        this.noticeSubType!,
        this.noticeTypeKonfiguration!,
        this.f.dataMap!,
        this.aendringsbekendtgoerelse
      );
    },
    // Byg og konfigurer den visuelle model
    async bygOgKonfigurerModel(defaultValues?: Record<string, string>, erAendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting) {
      // Rebuild kun model hvis den ikke allerede er bygget, eller hvis det er forced.
      // Vi instantierer vores visual model ud fra noticeType. Således den er komplet ASAP og kan sendes til backenden når vi vil.
      await this.resetModel(defaultValues);
      if (this.bekendtgoerelseNoegle == undefined) {
        throw new Error('bekendtgoerelseNoegle skal være defineret');
      }
      console.warn('Sætter this.model.aendringsbekendtgoerelseSetting til: ' + erAendringsbekendtgoerelse);
      this.model.aendringsbekendtgoerelseSetting = erAendringsbekendtgoerelse;
    },
    // Håndter speciel logik for ændringsbekendtgørelser
    async haandterAendringsbekendtgoerelsesLogik(aendringsbekendtgoerelseAf?: BekendtgoerelseNoegle, skalBrugeKladde?: boolean) {
      // Håndter eForms ændringsbekendtgørelse
      if (this.model.aendringsbekendtgoerelseSetting == AendringsbekendtgoerelseSetting.AENDRINGSBEKENDTGOERELSE_EFORMS && !skalBrugeKladde) {
        await this.haandterEFormsAendringsbekendtgoerelse(aendringsbekendtgoerelseAf);
      }

      // Håndter blank ændringsbekendtgørelse
      if (this.model.aendringsbekendtgoerelseSetting == AendringsbekendtgoerelseSetting.AENDRINGSBEKENDTGOERELSE_BLANK) {
        this.haandterBlankAendringsbekendtgoerelse();
      }
    },
    // Håndter eForms ændringsbekendtgørelse logik - kopierer data fra eksisterende bekendtgørelse
    async haandterEFormsAendringsbekendtgoerelse(aendringsbekendtgoerelseAf?: BekendtgoerelseNoegle) {
      console.debug('Vi bygger en ændringsbekendtgørelse ud fra eksisterende bekendtgørelse.');

      if (aendringsbekendtgoerelseAf?.noticeId?.value == undefined || aendringsbekendtgoerelseAf?.noticeVersion?.value === undefined) {
        throw new Error('aendringsbekendtgoerelseAf var dårlig: ' + JSON.stringify(aendringsbekendtgoerelseAf));
      }

      // 1. Hent bekendtgørelse
      const vm = await bekendtgoerelseService.hentBekendtgoerelseSomVisualModel(aendringsbekendtgoerelseAf);

      // 2. Apply ændringer
      for (const kc of this.konfigureretNoticeType!.content) {
        this.patchVisualModel(
          kc,
          vm,
          [
            FieldTerms.BT_04_NOTICE,
            FieldTerms.BT_03_NOTICE,
            FieldTerms.BT_02_NOTICE,
            FieldTerms.OPP_070_NOTICE,
            FieldTerms.BT_01_NOTICE
          ],
          [EformsConstants.GroupIdentifiers.CHANGE]
        );
      }

      // 3. Tilføj data til GR-Change
      // 3.1: Sæt 'BT-758-notice' til 'værdier for Notice Identifier (id "BT-701-notice") og Version Identifier (id "BT-757-notice")'
      const bt758noticeDestinationVisualModel = eformsDataUtil.findVisualModelNodeByPredicateRec(
        this.model,
        vmNode => vmNode.contentId === FieldTerms.BT_758_NOTICE
      );
      if (bt758noticeDestinationVisualModel === undefined) {
        throw new Error('Skal være defineret for en ændringsbekendtgørelse...');
      }
      bt758noticeDestinationVisualModel.value = `${aendringsbekendtgoerelseAf.noticeId.value}-${aendringsbekendtgoerelseAf.noticeVersion.value}`;
    },
    // Håndter blank ændringsbekendtgørelse logik
    haandterBlankAendringsbekendtgoerelse() {
      // MUD-2227: AK3: feltet Procedure Legal Basis (id "BT-01-notice") vises som et redigerbart forvalgsliste-felt med værdierne fra eForms kodelisten "legal-basis-dk". Feltet er ikke forudfyldt med en default værdi.
      for (const f of [FieldTerms.BT_01_NOTICE, FieldTerms.BT_04_NOTICE]) {
        const kc = eformsDataUtil.findKonfigureretContentRec(this.konfigureretNoticeType!.content, f);
        if (kc == undefined) {
          console.warn(`'${f}' kunne ikke blive fundet og konfigureret for AENDRINGSBEKENDTGOERELSE_BLANK`);
        } else {
          kc.readOnly = false;
          console.debug(`AENDRINGSBEKENDTGOERELSE_BLANK: Sætter '${f}' til at være readOnly=false`);
        }
      }
    },

    // Afslut initialiseringsprocessen
    async afslutInitialisering(kladdeBekendtgoerelseNoegle?: BekendtgoerelseNoegle) {
      // Opret kladde hvis vi ikke brugte en eksisterende
      if (!this.skalBrugeKladde(kladdeBekendtgoerelseNoegle)) {
        if (this.bekendtgoerelseNoegle == undefined) {
          throw new Error('bekendtgoerelseNoegle skal være defineret');
        }
        await bekendtgoerelseKladdeService.opretKladde(this.bekendtgoerelseNoegle, this.model);
      }

      // Vær sikker på at vi har sat currentSprog til den rette værdi.
      const currentSprogNode = eformsDataUtil.findInModel([this.model], FieldTerms.BT_702A_NOTICE);
      console.debug('Vi skifter sprog til: ' + currentSprogNode?.value);
      this.skiftSprog(currentSprogNode?.value ?? EformsConstants.Languages.DEFAULT);
    },
    // Validerer modellen hvis validering er aktiveret og opdaterer kladde
    async validerModelHvisValideringErTil(forceValider = false) {
      const eformsModelStore = useEformsModelStore(window.pinia);
      const sprog = eformsDataUtil.findInModel(eformsModelStore.model.children!, FieldTerms.BT_702A_NOTICE)?.value ?? '';
      console.debug('Sproget er måske ændret, opdater felter, der er afhængige (dem, der ender med "-Language").');
      eformsModelStore.skiftSprog(sprog);
      // Opdater kladde som promise
      if (this.bekendtgoerelseNoegle == undefined) {
        throw new Error('bekendtgoerelseNoegle skal være defineret');
      }
      const opdaterKladdePromise = bekendtgoerelseKladdeService.debouncedOpdaterKladde(this.bekendtgoerelseNoegle);
      if (!forceValider && !this.formularValideringer?.erKompletFormularValideringTil) {
        console.debug(`validerModel kaldt, men 'erKompletFormularValideringTil' er: ${this.formularValideringer?.erKompletFormularValideringTil}`);
        return;
      }
      // Kald backend få validering
      const config = AuthService.getConfig(Komponent.Udbud, false);
      const modelKopi = JSON.parse(JSON.stringify(this.model)) as VisualModelNode;
      eformsDataUtil.fjernOrphans(modelKopi);
      this.formularValideringer = {
        erKompletFormularValideringTil: true,
        erValideringKoert: false,
        feltFejl: this.formularValideringer.feltFejl, // Undgå at opdatere disse værdier
        andreFejl: this.formularValideringer.andreFejl // Undgå at opdatere disse værdier
      };
      const result = await getBekendtgoerelseController().valider1(this.sdkVersion, modelKopi, config);
      const feltFejl: Map<string, ValideringFejlDetailer> = new Map();
      const andreFejl: ValideringFejlDetailer[] = [];
      // Processér validerings resultater fra backend
      if (result.data.schematronValideringsFejlDto) {
        for (const f of result.data.schematronValideringsFejlDto) {
          const detailjer = {
            details: f.details,
            ruleId: f.ruleId
          } as ValideringFejlDetailer;
          if (skalFejlFiltreres(f, this.choiceGroupLookupHelper, this.model)) {
            console.debug(`Fejl med contentId ${f.contentId} og nodeId: ${f.nodeId} filtreres fra`);
            continue; // fejl filtreres fra, så vi fortsætter uden at tilføje liste
          }

          if (f.contentType == 'node') {
            const nodeId = f.contentId != '' ? f.contentId : f.nodeId;
            console.debug(`${nodeId} er node => andreFejl`);
            // Oversæt fra ND- => GR-
            if (nodeId?.startsWith('ND-')) {
              const r = eformsDataUtil.expandXmlNodeToFields(nodeId);
              const contentOrder = [] as string[];
              for (const c of this.konfigureretNoticeType!.content!) {
                contentOrder.push(...eformsDataUtil.contentOrder(c));
              }
              const intersection = _.intersection(r, contentOrder);
              const logMessage = `${nodeId}: alle felter i xml: ${JSON.stringify(
                r
              )} ............. felter i xml og i denne notice type: ${JSON.stringify(intersection)}`;

              if (intersection.length == 0) {
                console.warn(logMessage);
              } else {
                console.debug(logMessage);
              }
            }
            const key = `${nodeId}` + (f.contentCount != '' ? `-${f.contentCount}` : '');
            feltFejl.set(key, detailjer);
          } else if (f.contentId !== undefined && f.contentId !== '' && f.contentCount !== undefined && f.contentCount !== '') {
            feltFejl.set(`${f.contentId}-${f.contentCount}`, detailjer);
          } else {
            if (f.details !== undefined) {
              try {
                const parsedDetails = JSON.parse(f.details) as {
                  see?: string;
                  contentCount?: number;
                  diagnostic?: string;
                };
                if (parsedDetails?.see?.startsWith('node')) {
                  console.warn(`${f.contentId} er node => andreFejl`);
                } else if (parsedDetails.see != undefined) {
                  const seeSplit = parsedDetails.see.split(':');
                  if (seeSplit.length == 2) {
                    const btId = seeSplit[1];
                    if (parsedDetails.contentCount !== undefined) {
                      feltFejl.set(btId + '-' + parsedDetails.contentCount, detailjer);
                      console.debug('Kunne tilknytte anden fejl til specifik felt: ' + btId);
                    } else {
                      feltFejl.set(btId, detailjer);
                      console.debug('Kunne tilknytte anden fejl til uspecifik felt: ' + btId);
                    }
                    // Undlad at tilføje til andreFejl ...
                    continue;
                  }
                }
              } catch (err) {
                // Vi undlader at gøre noget her, lader den bare falde tilbage til andreFejl.
              }
            }
            andreFejl.push(detailjer);
          }
        }
      }
      // Tilføj client-side valideringer

      const straksvalideringer = eformsValidationUtil.validateNumberFields();

      // Opdater datastore med resultater fra validering
      this.formularValideringer = {
        erValideringKoert: true,
        erValideringUdenFejl: feltFejl.size === 0 && andreFejl.length === 0,
        feltFejl: new Map([...straksvalideringer, ...feltFejl]), // Bemærk overskrives fra venstre mod højre
        andreFejl: andreFejl,
        erKompletFormularValideringTil: this.formularValideringer.erKompletFormularValideringTil
      } as ValideringFejl;
      // Vær sikker på at vi har ventet på promiset
      await opdaterKladdePromise;
    },
    // Beregner og bygger den komplette visuelle model baseret på notice type og konfiguration
    beregnModel(defaultValues?: Record<string, string> | undefined): VisualModelNode {
      console.time('beregnModel');

      if (this.f.dataMap === undefined) {
        throw new Error('Fields dataMap burde ikke kunne være null efter this.f.promise er resolved. Tjek om fields.json blev hentet korrekt');
      }
      // Hvis vi bruger this.highestContentCountMap her, så vil der ske en masse change tracking. Dette gør løsningen meget langsom i nogle tilfælde.
      // I stedet laver en et midlertidigt map og assigner det bagefter
      const tempMap = {};
      const model = {
        contentId: EformsConstants.StandardIdentifiers.NOTICE_ROOT,
        visNodeId: EformsConstants.StandardIdentifiers.ND_ROOT,
        visType: EformsConstants.VisType.NON_FIELD,
        contentCount: '1',
        sdkVersion: `${this.noticeSubType!.metadataDatabase.version}`,
        noticeSubType: this.noticeSubType!.noticeId,
        noticeUuid: undefined, // Værdi sættes senere...
        children: [
          mapMetadata(this.noticeSubType!, this.noticeTypeKonfigurationFields!, this.noticeSubTypeList!),
          mapContent(
            this.noticeSubType!.content,
            this.noticeTypeKonfiguration!,
            tempMap,
            this.f.dataMap,
            this.noticeSubType,
            this.noticeSubTypeList,
            this.aendringsbekendtgoerelse
          )
        ]
      };
      // Brug det midlertidige map i modellen.
      this.highestContentCountMap = tempMap;
      console.timeEnd('beregnModel');
      // Anvend standard værdier hvis givet
      if (defaultValues !== undefined) {
        for (const [id, value] of Object.entries(defaultValues)) {
          const node = eformsDataUtil.findInModel(model.children, id);
          if (value === undefined) {
            console.debug("Satte ikke default værdi for '" + id + "' da value var undefined.");
          } else if (node !== undefined) {
            node.value = value;
            console.debug("Sætter default værdi for '" + id + "' til '" + value + "'");
          } else {
            console.error("Kunne ikke sætte default værdi for '" + id + "' til '" + value + "'");
          }
        }
      }
      return model;
    },
    // Nulstiller og genopbygger modellen fra bunden med standard værdier
    async resetModel(defaultValues?: Record<string, string> | undefined) {
      const promises = [] as Promise<void>[];
      if (!this.noticeSubType) {
        promises.push(this.initAndSetNoticeType(this.sdkVersion, this.noticeSubTypeId));
      }
      if (!this.noticeTypeKonfiguration) {
        promises.push(this.initNoticeTypeKonfiguration());
      }
      if (!this.f.data) {
        promises.push(this.initFields());
      }
      if (!this.noticeSubTypeList) {
        promises.push(this.initNoticeTypeList());
      }
      if (this.genbrug) {
        // Clear liste inden vi kalder init igen.
        this.genbrug!.egneBekendtgoerelserMetadata = undefined;
      }
      if (!this.genbrug?.egneBekendtgoerelserMetadata) {
        promises.push(this.initEgneBekendtgoerelser());
      }
      await Promise.allSettled(promises);

      this.highestContentCountMap = {};
      // Await fields promise hvis det findeds, så vi kan være sikker på at this.f.dataMap er sat.
      if (this.f.promise !== null) {
        await this.f.promise;
      }
      if (this.f.dataMap === undefined) {
        throw new Error('Fields dataMap burde ikke kunne være null efter this.f.promise er resolved. Tjek om fields.json blev hentet korrekt');
      }

      if (this.genbrug) {
        this.genbrug.hasAutofilledCvr = false;
      }
      // Nulstil model inden beregning, da dele af "beregnModel" kigger på nuværende model.
      this.model = {};

      this.model = this.beregnModel(defaultValues);
      this.saetNoticeId();
    },
    // Sætter notice ID og version i modellen og bekendtgørelse nøgle
    saetNoticeId() {
      const noticeUuid = this.model.children![0].children?.find(x => x.contentId === FieldTerms.BT_701_NOTICE)?.value;
      const noticeVersion = this.model.children![0].children?.find(x => x.contentId === FieldTerms.BT_757_NOTICE)?.value;
      if (noticeUuid === undefined) {
        throw new Error('Vi har lige reset model, men har ingen noticeUuid');
      }
      this.model.noticeUuid = noticeUuid;
      this.bekendtgoerelseNoegle = {
        noticeId: { value: noticeUuid },
        noticeVersion: { value: noticeVersion }
      } as BekendtgoerelseNoegle;
      console.debug('Opdaterer eformsModelStore til at have noticeId og version: ' + JSON.stringify(this.bekendtgoerelseNoegle));
    },
    // Sætter developer mode til og fra
    toggleDeveloperMode() {
      this.developerMode = !this.developerMode;
      console.debug(`Dev mode?: ${this.developerMode}`);
    },
    // Knap i toppen af opret indkøb, der toggler visning af businessterms (BT-xxx-xxx)
    toggleBusinessTermId() {
      this.visBusinessTermId = !this.visBusinessTermId;
    },
    // Knap i toppen af opret indkøb der toggler obligatoriske felter så "optional" felter ikke vises
    toggleObligatoriskeFelter() {
      this.visKunObligatoriskeFelter = !this.visKunObligatoriskeFelter;
    },
    toggleKompletFormularValidering() {
      console.debug(`toggleKompletFormularValidering => ${this.formularValideringer.erKompletFormularValideringTil}`);
      if (this.formularValideringer === undefined || this.formularValideringer.erKompletFormularValideringTil === false) {
        this.validerModelHvisValideringErTil(true);
      } else {
        this.formularValideringer = {
          erKompletFormularValideringTil: false,
          erValideringKoert: false,
          erValideringUdenFejl: false,
          andreFejl: undefined,
          feltFejl: undefined
        };
      }
    },
    // Opdaterer dele af modellen baseret på felt/gruppe ID
    updateModelById(contentId: string, value: string) {
      const toUpdate = eformsDataUtil.findInModel(this.model.children!, contentId);
      console.debug(`📘 Opdater data: '${value}' - old object: ${JSON.stringify(toUpdate)}`);
      if (toUpdate) {
        toUpdate.value = value;
        return;
      }
      console.error(`Kunne ikke opdatere data. ID: ${contentId} blev ikke fundet i visual model.`);
    },
    addRepeatable(content: KonfigureretContent, contextNode: VisualModelNode, validateMaybe = true): VisualModelNode[] {
      const nyRepeatable = eformsDataUtil.addRepeatableImpl(
        content,
        contextNode,
        this.model,
        this.noticeSubType!,
        this.noticeSubTypeList!,
        this.noticeTypeKonfiguration!,
        this.highestContentCountMap,
        this.f.dataMap!
      );

      if (validateMaybe) {
        this.validerModelHvisValideringErTil();
      }
      return nyRepeatable;
    },
    removeRepeatable(nodeToRemove: VisualModelNode, validateMaybe = true) {
      eformsDataUtil.removeRepeatableImpl(nodeToRemove, this.model, this.konfigureretNoticeType!);

      if (validateMaybe) {
        this.validerModelHvisValideringErTil();
      }
    },
    findNaesteVaerdiMedIdScheme(konfigureretContent: KonfigureretContent | Content): string {
      // På sigt skulle denne ikke kigge på this.model, da man kan være ved at resette osv.
      const allSiblings = eformsDataUtil.findAllDeepVisualModelNodes(konfigureretContent, this.model);
      const allValues = allSiblings.filter(x => x.value !== '').map(x => +x.value!.split('-')[1]);
      if (allValues.length > 0) {
        const maxValue = allValues.reduce((prev, current) => (prev && prev > current ? prev : current));
        return `${maxValue + 1}`.padStart(4, '0');
      }
      return '0001';
    },
    patchVisualModel(kcToReplace: KonfigureretContent, vm: VisualModelNode, forceOpdaterMetadata?: string[], skipKonfigureretContentId?: string[]) {
      // kcToReplace: Det konfigueret content som ønsker at overskrives.
      // vm: Det visual model data som skal bruges som udgangspunktet for overskrivningen
      console.debug(`patchVisualModel => ${kcToReplace.id}`);

      // Udfør ændringer på en kopi, som senere bliver replaced fuldstændig.
      const modelKopi: VisualModelNode = JSON.parse(JSON.stringify(this.model));

      // 1. Gemmemgå kc
      //    1. Find data i VM
      //    2. Håndter flyttesPaaTvaersAfXml (på samme måde som addRepeatable?)
      // const srcVmNode = eformsDataUtil.findInModel([vm], kcToReplace.visualModelParentId);
      // const destVmNode = eformsDataUtil.findInModel([modelKopi], kcToReplace.visualModelParentId);
      eformsDataUtil.patchDataRekursivt(kcToReplace, vm, vm, modelKopi, modelKopi, forceOpdaterMetadata, skipKonfigureretContentId);

      // Udskift modellen med ny replaced udgave.
      this.model = modelKopi;

      // Valider efter patch
      this.validerModelHvisValideringErTil();
    },
    // Rydder data i en specifik sektion af den visuelle model
    rydVisualModelData(kcToClear: KonfigureretContent): void {
      console.debug(`rydVisualModelData => ${kcToClear.id}`);

      // Lav ren model vi kan udføre replacement med
      const renModel = this.beregnModel();

      // Opdater model.
      this.patchVisualModel(kcToClear, renModel);
    },
    // Tjekker om kladden er ændret siden den sidste gemte kladde
    isKladdeDirty(): boolean {
      if (this.kladde.lastSavedKladde == undefined) {
        return true;
      }
      return JSON.stringify(this.model) != this.kladde.lastSavedKladde;
    },
    updateKladdeBookkeepingSaveInProgress(): void {
      this.kladde.scheduled = false;
      this.kladde.lastSavedAt = Date.now();
    },
    updateKladdeBookkeepingSaveDone(): void {
      this.kladde.lastSavedKladde = JSON.stringify(this.model);
      this.kladde.lastSavedAt = Date.now();
      this.kladde.continuousErrorCount = 0;
      this.kladde.scheduled = false;
    },
    incrementKladdeErrorCounter(): void {
      this.kladde.continuousErrorCount += 1;
      this.kladde.scheduled = false;
    },
    // Sørger for at give muligheden for at skifte sprog på frontenden
    skiftSprog(nytSprog: string): void {
      // Hvis brugeren ændrer 'BT-702(a)-notice' så skal vi opdatere alle attributterne med navn languageID til det nye sprog, så formularen er konsistent.
      this.currentLanguageId = nytSprog;
      // finder alle felter hvor der kan skiftes sprog baseret på languageID attribute
      const alleFelterDerOpdateres: VisualModelNode[] = [];
      eformsDataUtil.findAllVisualModelNodeByPredicateRec(
        this.model,
        vm => {
          const field = this.f.dataMap?.get(vm.contentId!);
          return field != undefined && field.attributeName == 'languageID';
        },
        alleFelterDerOpdateres
      );
      // opdaterer alle de fundne felter med ny language attribute
      for (const vm of alleFelterDerOpdateres) {
        vm.value = nytSprog;
      }
      console.debug(`Opdateret formular til nyt sprog: '${nytSprog}'`);
    },
    bygChoiceGroupLookupHelper(ntk: NoticeTypeKonfiguration): Record<string, ChoiceGroupLookup> {
      this.choiceGroupLookupHelper = {} as Record<string, ChoiceGroupLookup>;
      [...(ntk.groups?.added ?? []), ...(ntk.groups?.overwritten ?? [])].forEach(entry => {
        if (entry.choiceGroupConfiguration) {
          entry.choiceGroupConfiguration?.children?.forEach(choiceGroupChild => {
            choiceGroupChild.children?.forEach(id => {
              this.choiceGroupLookupHelper![id] = {
                parentId: choiceGroupChild.id,
                choiceGroupId: entry.id
              };
            });
          });
        }
      });
      return this.choiceGroupLookupHelper;
    }
  },
  getters: {
    findData: (state: EFormsStoreModel) => (id: string) => eformsDataUtil.findInModel(state.model.children!, id),
    getNoticeId: (state: EFormsStoreModel) => () => eformsDataUtil.findInModel(state.model.children!, FieldTerms.BT_701_NOTICE)?.value ?? '',
    getNoticeVersion: (state: EFormsStoreModel) => () => eformsDataUtil.findInModel(state.model.children!, FieldTerms.BT_757_NOTICE)?.value ?? '01',
    getNoticePublicationNumber: (state: EFormsStoreModel) => () => eformsDataUtil.findInModel(state.model.children!, FieldTerms.OPP_010_NOTICE)?.value ?? '',
    findDataMedContentCount: (state: EFormsStoreModel) => (id: string, contentCount: string) =>
      eformsDataUtil.findInModelMedContentCount(state.model.children!, id, contentCount)
  }
});

function getNextContentCount(id: string, highestContentCountMap: Record<string, number>): string {
  if (highestContentCountMap[id] === undefined) {
    highestContentCountMap[id] = 0;
  }
  highestContentCountMap[id] += 1;
  return highestContentCountMap[id].toString();
}
// "Oversætter" metadata fra noticetype til visual model struktur
function mapMetadata(
  noticeType: NoticeType,
  noticeTypeKonfigurationFields: FieldKonfiguration[],
  noticeSubTypeList: NoticeTypeList
): VisualModelNode {
  const visualModelMetadata = {
    contentId: EformsConstants.StandardIdentifiers.NOTICE_METADATA,
    contentType: EformsConstants.ContentType.METADATA_CONTAINER,
    contentCount: '1',
    visType: EformsConstants.VisType.NON_FIELD,
    children: noticeType.metadata.map(x => {
      const content = {
        contentId: x.id,
        contentType: x.contentType,
        contentCount: '1',
        visType: x.contentType,
        value: ''
      } as VisualModelNode;
      overskrivFeltVaerdi(noticeTypeKonfigurationFields, x, content, noticeType, noticeSubTypeList);
      return content;
    })
  };

  return visualModelMetadata;
}
// mapper alt content fra noticeType til visual model
function mapContent(
  content: Content[],
  noticeTypeKonfiguration: NoticeTypeKonfiguration,
  highestContentCountMap: Record<string, number>,
  fieldMap: Map<string, Field>,
  noticeType?: NoticeType,
  noticeSubTypeList?: NoticeTypeList,
  aendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting
): VisualModelNode {
  // Mapper rod elementet for data i bekendtgørelsen
  return {
    contentId: EformsConstants.ContentType.DATA_CONTAINER,
    contentType: EformsConstants.ContentType.DATA_CONTAINER,
    contentCount: '1',
    visType: EformsConstants.VisType.NON_FIELD,
    children: mapSubContent(
      content,
      noticeTypeKonfiguration,
      highestContentCountMap,
      true,
      fieldMap,
      noticeType,
      noticeSubTypeList,
      aendringsbekendtgoerelse
    )
  };
}
// Mapper sub-content rekursivt til visual model nodes
export function mapSubContent(
  contentList: Content[],
  noticeTypeKonfiguration: NoticeTypeKonfiguration,
  highestContentCountMap: Record<string, number>,
  erLavRepeatable: boolean,
  fieldMap: Map<string, Field>,
  noticeType?: NoticeType,
  noticeSubTypeList?: NoticeTypeList,
  aendringsbekendtgoerelse?: AendringsbekendtgoerelseSetting
): VisualModelNode[] {
  const visualModelContent = [] as VisualModelNode[];
  const noticeTypeKonfigurationGroups = [...(noticeTypeKonfiguration.groups?.added || []), ...(noticeTypeKonfiguration.groups?.overwritten || [])];
  const noticeTypeKonfigurationFields = [...(noticeTypeKonfiguration.fields?.added || []), ...(noticeTypeKonfiguration.fields?.overwritten || [])];

  contentList.forEach(content => {
    // Håndter gruppe content
    if (content.contentType === EformsConstants.ContentType.GROUP) {
      // Hvis den er repeatable, lav kun en kopi hvis det ønskes ...
      // Under manuel oprettelse af ny repeatable, undlad at springe over.
      // TODO: Skal vi håndtere dette anderledes?
      if (content._repeatable) {
        console.debug(`mapSubContent _repeatable: ${content.id}`);
        const konfigureretContent = noticeTypeKonfigurationGroups.find(x => x.id === content.id);
        if (konfigureretContent !== undefined) {
          if (konfigureretContent.opretKunManuelt !== undefined && konfigureretContent.opretKunManuelt && !erLavRepeatable) {
            console.debug('Undlader at lave repeatable for: ' + content.id + ' fordi opretKunManuelt=true');
            return;
          }

          if (!erLavRepeatable) {
            console.debug(`mapSubContent - ${content.id} | konfigureretContent.defaultRepeats => ${konfigureretContent.defaultRepeats}`);
            if (konfigureretContent.defaultRepeats !== undefined && konfigureretContent.defaultRepeats === 0) {
              return;
            }
          }
        }
      }
      // Spring over hvis gruppen skal fjernes fra visual model
      const konfigureretContent = noticeTypeKonfigurationGroups.find(x => x.id === content.id);
      if (konfigureretContent?.removeFromVisualModel !== undefined && konfigureretContent.removeFromVisualModel === true) {
        console.debug(`Undlader at oprette: ${content.id} da den er removeFromVisualModel=true`);
        return;
      }

      if (aendringsbekendtgoerelse !== undefined && aendringsbekendtgoerelse === AendringsbekendtgoerelseSetting.IKKE_AENDRINGSBEKENDTGOERELSE) {
        if (content.id === 'GR-Change') {
          console.debug(`Undlader at oprette: ${content.id} da den er en del af ændringsbekendtgørelse, men dette er ikke en ændringsbekendtgørelse`);
          return;
        }
      }

      // Tilføj kun en knude i visualModel hvis gruppen har et nodeId.
      if (content.nodeId != null) {
        visualModelContent.push({
          contentId: content.id,
          contentType: content.contentType,
          contentCount: getNextContentCount(content.id, highestContentCountMap),
          children: content.content != null ? mapSubContent(content.content, noticeTypeKonfiguration, highestContentCountMap, false, fieldMap) : [],
          visType: EformsConstants.VisType.NON_FIELD,
          visNodeId: content.nodeId
        } as VisualModelNode);
      } else if (content.content) {
        mapSubContent(content.content, noticeTypeKonfiguration, highestContentCountMap, false, fieldMap).forEach(child =>
          visualModelContent.push(child)
        );
      }
    }
    // Håndter felt content
    else if (content.contentType === EformsConstants.ContentType.FIELD) {
      const visualModelNode = {
        contentId: content.id,
        contentType: content.contentType,
        contentCount: getNextContentCount(content.id, highestContentCountMap),
        visType: EformsConstants.VisType.FIELD,
        value: ''
      } as VisualModelNode;

      overskrivFeltVaerdi(noticeTypeKonfigurationFields, content, visualModelNode, noticeType, noticeSubTypeList);
      visualModelContent.push(visualModelNode);

      const field = fieldMap.get(content.id);
      if (field?.attributes !== undefined && field.attributes.length > 0) {
        // Hvis feltet har attributes, lav da en knude til den ved siden af til den.
        for (const attr of field.attributes) {
          const attrField = fieldMap.get(attr);
          if (attrField === undefined) {
            throw new Error('Forventede at attributten var at findes i fields.json, SDK er inkonsistent...');
          }
          // Findes der allerede en attribut for denne?
          if (visualModelContent.find(x => x.contentId == attrField.id && x.contentCount == visualModelNode.contentCount)) {
            continue;
          }

          const override = noticeTypeKonfigurationFields.find(x => x.id === attrField.id);

          let value = override?.presetValue ?? attrField.presetValue;

          const isLanguageField = attrField.attributeName === 'languageID';
          if (value === undefined && isLanguageField) {
            const eformsModelStore = useEformsModelStore(window.pinia);
            value = eformsModelStore.currentLanguageId ?? EformsConstants.Languages.DEFAULT;
          }

          if (value == undefined || value === '') {
            // Måske dette felt er destination af en kopierTil?
            const kopierTilSrc = noticeTypeKonfigurationFields.find(x => x.kopierTil == attrField.id);
            if (kopierTilSrc !== undefined) {
              value = kopierTilSrc.presetValue ?? '';
              console.debug(`🧾 KopierTil fundet som destination: ${attrField.id} får presetValue: ${kopierTilSrc.presetValue}`);
            }
          } else if (value != 'DAN' && value != '' && value != attrField.presetValue) {
            console.debug(`🧾 Attribut '${attrField.id}' får presetValue: '${value}' fra NTK`);
          }
          const attributeVisualModelNode = {
            contentId: attrField.id,
            contentType: EformsConstants.ContentType.FIELD,
            contentCount: getNextContentCount(attrField.id, highestContentCountMap),
            visType: EformsConstants.VisType.FIELD,
            value: value ?? ''
          } as VisualModelNode;
          visualModelContent.push(attributeVisualModelNode);
        }
      }
    } else {
      console.error(`😡Kender ikke content-type: '${content.contentType}' ???`);
    }
  });

  return visualModelContent;
}
// Overskriver felt værdi baseret på konfiguration og preset værdier
export function overskrivFeltVaerdi(
  noticeTypeKonfigurationFields: FieldKonfiguration[],
  content: Content | Metadata,
  visualModelNode: VisualModelNode,
  noticeType?: NoticeType,
  noticeSubTypeList?: NoticeTypeList
) {
  // tjekker om der er en konfigureret presetValue
  const feltIKonfiguration = noticeTypeKonfigurationFields.find(fieldKonfig => fieldKonfig.id === content.id);
  if (feltIKonfiguration?.presetValue != null) {
    console.debug(`🧾 Sætter value på '${content.id}' til: '${feltIKonfiguration.presetValue}' - Fra NTK`);
    visualModelNode.value = feltIKonfiguration.presetValue;
    const processedValue = processerDynamiskePresetValues(
      feltIKonfiguration.presetValue,
      content,
      visualModelNode,
      noticeType,
      noticeSubTypeList
    );

    if (processedValue !== null) {
      visualModelNode.value = processedValue;
    } else {
      console.debug(`🧾 Sætter value på '${content.id}' til: '${feltIKonfiguration.presetValue}' - Fra NTK (static)`);
      visualModelNode.value = feltIKonfiguration.presetValue;
    }

    if (feltIKonfiguration.kopierTil !== undefined) {
      console.debug('Ville kopiereTil ud fra presetValue');
    }
    return;
  }
  // tjekker om der er en preset value fra SKD'en
  if (content._presetValue) {
    if (content._presetValue.indexOf('{') === -1) {
      console.debug(`🧾 Sætter value på '${content.id}' til: '${content._presetValue}' - Fra SDK`);
      visualModelNode.value = content._presetValue;
    } else {
      console.debug(`🔴 Kan ikke håndtere presetValue på '${content.id}: '${content._presetValue}`);
    }
    return;
  }

  const eformsModelStore = useEformsModelStore(window.pinia);
  const field = eformsModelStore.f.dataMap!.get(content.id)!;
  if (field.type === 'id' && field.idScheme !== undefined) {
    visualModelNode.value = field.idScheme + '-' + eformsModelStore.findNaesteVaerdiMedIdScheme(content);
    console.debug(`Sætter automatisk ID på: ${field.id} => '${visualModelNode.value}'`);
  }
}
// Håndterer alle specielle cases baseret på dynamisk preset value (findes som "{{xxxx}}" i noticeTypekonfiguration
export function processerDynamiskePresetValues(
  presetValue: string,
  content: Content | Metadata,
  visualModelNode: VisualModelNode,
  noticeType?: NoticeType,
  noticeSubTypeList?: NoticeTypeList
): string | null {
  // tjek om feltværdien er en "dynamisk" værdi
  if (!presetValue.startsWith('{{') || !presetValue.endsWith('}}')) {
    return null;
  }
  let subtypeDefinition;
  let typeDefinition;
  const dynamicType = presetValue.slice(2, -2);
  const eformsModelStore = useEformsModelStore(window.pinia);

  switch (dynamicType) {
    case 'uuid':
      return eformsUtil.generateUuid();

    case 'nowDate':
      return dateUtil.nowDateAsCopenhagen();

    case 'nowTime':
      return dateUtil.nowTimeAsCopenhagen();

    case 'ublVersion':
      return noticeType?.ublVersion || '';

    case 'sdkVersion':
      return eformsModelStore.sdkVersion;

    case 'noticeId':
      return noticeType?.noticeId || '';

    case 'formType':
      if (!noticeSubTypeList || !noticeType) return '';
      subtypeDefinition = noticeSubTypeList.noticeSubTypes.find(
        subtype => noticeType.noticeId === subtype.subTypeId
      );
      return subtypeDefinition?.formType || '';

    case 'type':
      if (!noticeSubTypeList || !noticeType) return '';
      typeDefinition = noticeSubTypeList.noticeSubTypes.find(
        subtype => noticeType.noticeId === subtype.subTypeId
      );
      return typeDefinition?.type || '';

    case 'version01':
      return '01';

    case 'currentLanguage':
      return eformsModelStore.currentLanguageId ?? EformsConstants.Languages.DEFAULT;

    case 'empty':
      return '';

    case 'keep-existing':
      if (visualModelNode.value !== undefined && visualModelNode.value !== '') {
        console.debug(`${content.id} var allerede sat!`);
        return visualModelNode.value;
      }
      return '';

    default:
      console.warn(`Unknown dynamic preset value type: '${dynamicType}' for field '${content.id}'`);
      return '';
  }
}

export function initHighestContentCountMap(model: VisualModelNode): Record<string, number> {
  const result: Record<string, number> = {};
  inithighestContentCountMapRec(model, result);
  return result;
}

export function inithighestContentCountMapRec(node: VisualModelNode, result: Record<string, number>) {
  if (node.children) {
    for (const child of node.children) {
      const key = `${child.contentId!}`;
      if (result[key] == undefined) {
        result[key] = 1;
      }
      const previousValue = child.contentCount == undefined ? 1 : +child.contentCount;
      result[key] = Math.max(result[key], previousValue);
      if (child.children) {
        inithighestContentCountMapRec(child, result);
      }
    }
  }
}
